import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowRightIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/solid";
import { passwordStrengthChecker, getPasswordColor } from "./utils/passwordStrengthChecker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Professional, trusted, and secure Auth Page
 * - Crisp visual hierarchy
 * - Clear labels & accessible inputs
 * - Password strength meter + live requirements
 * - First-class error states
 * - Social sign-in (Google) entry point
 * - Terms & Privacy consent on sign-up
 * - Profile photo optional (moved out of the critical path)
 * - Optional location gating is removed from the primary flow (can be asked later)
 */
export default function AuthPage({ type }) {
  const isLogin = type === "login";
  const navigate = useNavigate();

  // form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [profileFile, setProfileFile] = useState(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [emailStatus, setEmailStatus] = useState('idle');

  // ui state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // password helpers
  const pwdStrength = useMemo(() => (password ? passwordStrengthChecker(password) : ""), [password]);

  const pwdReqs = useMemo(() => {
    const length = password.length >= 8;
    const upper = /[A-Z]/.test(password);
    const lower = /[a-z]/.test(password);
    const number = /\d/.test(password);
    const symbol = /[^A-Za-z0-9]/.test(password);
    return { length, upper, lower, number, symbol };
  }, [password]);

  useEffect(() => {
    setError(null);
  }, [email, password, confirmPassword]);

  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProfileFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setProfileImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const toBase64 = async (file) => {
    if (!file) return null;
    const reader = new FileReader();
    return new Promise((resolve) => {
      reader.onloadend = () => {
        const res = reader.result?.toString() || "";
        const base64 = res.includes(",") ? res.split(",")[1] : res;
        resolve(base64);
      };
      reader.readAsDataURL(file);
    });
  };
  // Check if an email is already registered (best-effort; falls back to server error)
  const checkEmailExists = async (address) => {
    if (!address) return false;
    try {
      setEmailStatus('checking');
      const res = await fetch(`${BACKEND_URL}/auth/check-email?email=${encodeURIComponent(address)}`);
      if (res.ok) {
        const j = await res.json();
        const exists = !!j?.exists;
        setEmailStatus(exists ? 'exists' : 'available');
        return exists;
      }
      // If endpoint not available, don't block sign up
      setEmailStatus('error');
      return false;
    } catch (e) {
      setEmailStatus('error');
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setNotice(null);

    if (!email.trim() || !password) {
      setError("Email and password are required.");
      return;
    }

    if (!isLogin) {
      if (password !== confirmPassword) {
        setError("Passwords don’t match.");
        return;
      }
      if (!acceptTerms) {
        setError("Please accept the Terms and Privacy Policy to continue.");
        return;
      }
    }

    setLoading(true);
    try {
      if (!isLogin) {
        const exists = await checkEmailExists(email.trim().toLowerCase());
        if (exists) {
          setError("An account with this email already exists. Try logging in or reset your password.");
          setLoading(false);
          return;
        }
      }
      const profileImage = await toBase64(profileFile); // optional

      const body = {
        email: email.trim().toLowerCase(),
        password,
        ...(isLogin
          ? {}
          : {
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              profileImage,
            }),
      };

      const res = await fetch(`${BACKEND_URL}/auth/${isLogin ? "login" : "register"}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (res.status === 409) {  // conflict from server
        setError(data?.message || "An account with this email already exists.");
        setEmailStatus('exists');
        setLoading(false);
        return;
      }
      if (!res.ok) throw new Error(data?.message || "Something went wrong. Please try again.");

      if (isLogin) {
        // NOTE: Prefer httpOnly secure cookies server-side. LocalStorage is shown for continuity only.
        localStorage.setItem("token", data.token);
        localStorage.setItem("userEmail", email.trim().toLowerCase());
        localStorage.setItem("justLoggedIn", "true");
        const username = email.split("@")[0];
        navigate(`/${username}/explore`);
      } else {
        setNotice("Signup successful! You can log in now.");
        navigate("/login");
      }
    } catch (err) {
      const msg = (err?.message || "").toLowerCase();
      if (!isLogin && (msg.includes("exist") || msg.includes("duplicate"))) {
        setError("An account with this email already exists. Try logging in or reset your password.");
        setNotice(null);
        setEmailStatus('exists');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  
  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-slate-50 to-white">
      <header className="fixed left-6 top-6 z-50">
        <a
  href="https://frontera.me"
  target="_blank"
  rel="noopener noreferrer"
  aria-label="Visit frontera.me"
  className="group relative inline-flex items-center gap-5 rounded-[32px]
             border border-white/20 bg-white/10 px-6 py-4
             shadow-[0_0_0_1px_rgba(255,255,255,0.18)_inset,0_24px_64px_rgba(0,0,0,0.25)]
             backdrop-blur-2xl backdrop-saturate-150
             transition-[transform,box-shadow,background] duration-300 ease-out
             hover:shadow-[0_20px_80px_rgba(56,189,248,0.25)]
             hover:bg-white/14
             active:scale-[0.98]
             motion-safe:hover:-rotate-[0.25deg]
             focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-sky-500/30"
>
  {/* ambient glow/halo */}
  <div className="pointer-events-none absolute -inset-10 -z-10 rounded-[40px]
                  bg-[conic-gradient(from_180deg_at_50%_50%,rgba(56,189,248,0.18),rgba(99,102,241,0.14),rgba(217,70,239,0.18),rgba(56,189,248,0.18))]
                  blur-3xl transition-opacity duration-300 ease-out
                  group-hover:opacity-100" />

  {/* inner glass ring + soft top highlight */}
  <div className="pointer-events-none absolute inset-0 rounded-[32px] ring-1 ring-white/45 ring-inset" />
  <div className="pointer-events-none absolute inset-0 rounded-[32px] opacity-80
                  bg-gradient-to-b from-white/60 to-white/5
                  [mask-image:radial-gradient(120%_120%_at_50%_-10%,#000_32%,transparent_62%)]" />

  {/* sheen sweep (slides across on hover) */}
  <span
    aria-hidden
    className="pointer-events-none absolute inset-0 overflow-hidden rounded-[32px]"
  >
    <span
      className="absolute -left-1/3 top-0 h-full w-1/2
                 -translate-x-[120%] opacity-0
                 bg-[linear-gradient(110deg,transparent,rgba(255,255,255,0.65),transparent)]
                 transition duration-700 ease-out
                 group-hover:translate-x-[220%] group-hover:opacity-100"
    />
  </span>

  <img
    src="/fronteraLogo.png"
    alt="Frontera"
    className="h-14 w-auto md:h-16 lg:h-20
               drop-shadow-[0_6px_18px_rgba(0,0,0,0.25)]
               will-change-transform"
  />

  <div className="hidden sm:block">
    <div className="text-[10px] font-medium tracking-[0.25em] text-black/70">
      POWERED BY
    </div>
    <div className="text-2xl md:text-3xl font-extrabold tracking-tight text-black
                    drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]">
      Frontera
    </div>
  </div>
</a>

      </header>


      <main className="mx-auto grid min-h-screen max-w-6xl grid-cols-1 md:grid-cols-2 mt-16">
        {/* Left / Trust Panel */}
<section className="hidden md:flex flex-col justify-center p-12 pr-16 relative">
  {/* soft decorative glow */}
  <div className="pointer-events-none absolute -inset-8 -z-10 bg-gradient-to-tr from-sky-100 via-indigo-100 to-fuchsia-100 blur-2xl opacity-70" />
  
  <div className="relative max-w-md">
    <h1 className="text-4xl font-semibold tracking-tight text-slate-900">
      {isLogin ? "Pick up where you left off" : "Create your Frontera account"}
    </h1>
    <p className="mt-3 text-slate-600">
      {isLogin
        ? "Sign in to continue your projects and conversations."
        : "Create your profile and start exploring in under a minute."}
    </p>

    {/* concise, trustworthy points */}
    <ul className="mt-8 space-y-4">
      <li className="flex items-start gap-3">
        <CheckCircleIcon className="mt-0.5 h-5 w-5 text-sky-600" />
        <div>
          <p className="font-medium text-slate-900">Email confirmation</p>
          <p className="text-sm text-slate-600">
            We’ll send a verification link right after sign-up.
          </p>
        </div>
      </li>
      <li className="flex items-start gap-3">
        <CheckCircleIcon className="mt-0.5 h-5 w-5 text-sky-600" />
        <div>
          <p className="font-medium text-slate-900">Clear, simple onboarding</p>
          <p className="text-sm text-slate-600">
            No surprises—just the essentials to get you in quickly.
          </p>
        </div>
      </li>
      <li className="flex items-start gap-3">
        <CheckCircleIcon className="mt-0.5 h-5 w-5 text-sky-600" />
        <div>
          <p className="font-medium text-slate-900">Help when you need it</p>
          <a
  href={`mailto:support@frontera.app?subject=${encodeURIComponent("Support Request")}&body=${encodeURIComponent("Hi Frontera support,\n\nI need help with...")}`}
  className="text-sky-700 hover:underline"
>
  Contact support
</a>

        </div>
      </li>
    </ul>

    {/* Status & policies panel (replaces the “social proof” card) */}
<div className="mt-10 rounded-xl border border-slate-200 bg-white/70 p-4 shadow-sm">
  <p className="text-sm font-semibold text-slate-900">Getting started</p>
  <ol className="mt-3 space-y-2 text-sm text-slate-700">
    <li className="flex items-start gap-2">
      <span className="mt-1 h-2 w-2 rounded-full bg-sky-600"></span>
      Verify your email from the link we send.
    </li>
    <li className="flex items-start gap-2">
      <span className="mt-1 h-2 w-2 rounded-full bg-sky-600"></span>
      Add your name and interests (optional).
    </li>
    <li className="flex items-start gap-2">
      <span className="mt-1 h-2 w-2 rounded-full bg-sky-600"></span>
      Start exploring projects.
    </li>
  </ol>
</div>


  </div>
</section>


        {/* Right / Form Card */}
        <section className="flex items-center justify-center p-6 md:p-12">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white/80 shadow-lg backdrop-blur">
            <div className="border-b border-slate-100 px-6 py-5">
              <h2 className="text-xl font-semibold text-slate-900">
                {isLogin ? "Log in to Frontera" : "Sign up for Frontera"}
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                {isLogin ? (
                  <>New here? <Link className="text-sky-600 hover:underline" to="/signup">Create an account</Link></>
                ) : (
                  <>Already have an account? <Link className="text-sky-600 hover:underline" to="/login">Log in</Link></>
                )}
              </p>
            </div>

            {/* Error / Notice banners */}
            {error && (
              <div className="mx-6 mt-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <ExclamationCircleIcon className="mt-0.5 h-5 w-5" />
                <div>
                  <strong className="block">We couldn’t complete that.</strong>
                  <span className="block">{error}</span>
                </div>
              </div>
            )}
            {notice && (
              <div className="mx-6 mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
                {notice}
              </div>
            )}

            <div className="px-6 pb-6 pt-4">
              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                {!isLogin && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="firstName" className="mb-1 block text-sm font-medium text-slate-700">First name</label>
                      <input
                        id="firstName"
                        type="text"
                        autoComplete="given-name"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 placeholder-slate-400 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                    required
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="mb-1 block text-sm font-medium text-slate-700">Last name</label>
                      <input
                        id="lastName"
                        type="text"
                        autoComplete="family-name"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 placeholder-slate-400 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="mb-1 block text-sm font-medium text-slate-700">Email</label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 placeholder-slate-400 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (!isLogin) setEmailStatus('idle'); }}
                    onBlur={(e) => { if (!isLogin) checkEmailExists(e.target.value.trim().toLowerCase()); }}
                    required
                  />
                </div>

                {/* Email availability status */}
                {!isLogin && emailStatus === 'checking' && (
                  <p className="mt-1 text-xs text-slate-500 flex items-center gap-1"><ArrowPathIcon className="h-3 w-3 animate-spin" /> Checking…</p>
                )}
                {!isLogin && emailStatus === 'available' && (
                  <p className="mt-1 text-xs text-emerald-600">Looks good — email is available.</p>
                )}
                {!isLogin && emailStatus === 'exists' && (
                  <p className="mt-1 text-xs text-red-600">An account with this email already exists. <Link className="underline" to="/login">Log in</Link> or <Link className="underline" to="/forgot-password">reset your password</Link>.</p>
                )}

                {/* Passwords */}
{isLogin ? (
  <div>
    <label htmlFor="password" className="mb-1 block text-sm font-medium text-slate-700">Password</label>
    <div className="relative">
      <input
        id="password"
        type={showPassword ? "text" : "password"}
        autoComplete="current-password"
        className="w-full rounded-lg border border-slate-300 px-3 py-2 pr-10 text-slate-900 placeholder-slate-400 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
      />
      <button
        type="button"
        onClick={() => setShowPassword((s) => !s)}
        className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500 hover:text-slate-700"
        aria-label={showPassword ? "Hide password" : "Show password"}
      >
        {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
      </button>
    </div>
  </div>
) : (
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
    <div>
      <label htmlFor="password" className="mb-1 block text-sm font-medium text-slate-700">Create password</label>
      <div className="relative">
        <input
          id="password"
          type={showPassword ? "text" : "password"}
          autoComplete="new-password"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 pr-10 text-slate-900 placeholder-slate-400 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <button
          type="button"
          onClick={() => setShowPassword((s) => !s)}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500 hover:text-slate-700"
          aria-label={showPassword ? "Hide password" : "Show password"}
        >
          {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
        </button>
      </div>
      {/* Strength */}
      <small
        className="mt-1 block text-xs font-medium"
        style={{ color: getPasswordColor(pwdStrength || "Very Weak") }}
      >
        {pwdStrength || "Very Weak"}
      </small>
    </div>

    <div>
      <label htmlFor="confirm" className="mb-1 block text-sm font-medium text-slate-700">Confirm password</label>
      <div className="relative">
        <input
          id="confirm"
          type={showConfirm ? "text" : "password"}
          autoComplete="new-password"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 pr-10 text-slate-900 placeholder-slate-400 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
        />
        <button
          type="button"
          onClick={() => setShowConfirm((s) => !s)}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500 hover:text-slate-700"
          aria-label={showConfirm ? "Hide password" : "Show password"}
        >
          {showConfirm ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
        </button>
      </div>
    </div>
  </div>
)}


                {/* Requirements checklist (signup) */}
                {!isLogin && (
                  <ul className="grid grid-cols-2 gap-1 text-xs text-slate-600">
                    <li className="flex items-center gap-1">{pwdReqs.length ? <CheckCircleIcon className="h-4 w-4 text-emerald-500" /> : <ExclamationCircleIcon className="h-4 w-4 text-slate-300" />} At least 8 characters</li>
                    <li className="flex items-center gap-1">{pwdReqs.upper ? <CheckCircleIcon className="h-4 w-4 text-emerald-500" /> : <ExclamationCircleIcon className="h-4 w-4 text-slate-300" />} Uppercase letter</li>
                    <li className="flex items-center gap-1">{pwdReqs.lower ? <CheckCircleIcon className="h-4 w-4 text-emerald-500" /> : <ExclamationCircleIcon className="h-4 w-4 text-slate-300" />} Lowercase letter</li>
                    <li className="flex items-center gap-1">{pwdReqs.number ? <CheckCircleIcon className="h-4 w-4 text-emerald-500" /> : <ExclamationCircleIcon className="h-4 w-4 text-slate-300" />} Number</li>
                    <li className="flex items-center gap-1">{pwdReqs.symbol ? <CheckCircleIcon className="h-4 w-4 text-emerald-500" /> : <ExclamationCircleIcon className="h-4 w-4 text-slate-300" />} Symbol</li>
                  </ul>
                )}

                {/* Optional profile photo (signup) */}
                {!isLogin && (
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Profile photo <span className="text-slate-400">(optional)</span></label>
                    <div className="flex items-center gap-3">
                      <label className="relative inline-flex h-12 cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 text-sm text-slate-600 hover:bg-slate-50">
                        <input type="file" accept="image/*" className="absolute inset-0 h-full w-full cursor-pointer opacity-0" onChange={onPickImage} />
                        Upload image
                      </label>
                      {profileImagePreview && (
                        <img src={profileImagePreview} alt="Preview" className="h-10 w-10 rounded-full object-cover ring-2 ring-slate-200" />
                      )}
                    </div>
                  </div>
                )}

                {/* Terms */}
                {!isLogin && (
                  <label className="flex items-start gap-2 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                    />
                    <span>
                      I agree to the <a href="/legal/terms" className="text-sky-600 hover:underline">Terms</a> and <a href="/legal/privacy" className="text-sky-600 hover:underline">Privacy Policy</a>.
                    </span>
                  </label>
                )}

                {/* Remember me (login) */}
                {isLogin && (
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm text-slate-600">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                      Remember me on this device
                    </label>
                    <Link to="/forgot-password" className="text-sm font-medium text-sky-700 hover:underline">Forgot password?</Link>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="group relative mt-1 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-300 disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <ArrowPathIcon className="h-5 w-5 animate-spin" />
                      Processing…
                    </>
                  ) : (
                    <>
                      {isLogin ? "Log in" : "Create account"}
                      <ArrowRightIcon className="h-5 w-5 opacity-80" />
                    </>
                  )}
                </button>

                <p className="pt-2 text-center text-xs text-slate-500">
  This page is protected by Frontera. See our{" "}
  <Link to="/legal/privacy" className="text-sky-600 hover:underline">
    Privacy Policy
  </Link>{" "}
  and{" "}
  <Link to="/legal/terms" className="text-sky-600 hover:underline">
    Terms of Service
  </Link>.
</p>

              </form>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
